import React, { useState } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  StepConnector,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { UploadFile, AddLocationAlt, AddCard } from "@mui/icons-material";
// import DashboardHeader from "@components/Header/DashboardHeader";
import GenericBreadCrums from "@components/Breadcrumbs";
import MapIcon from "@mui/icons-material/Map";
import CloseIcon from "@mui/icons-material/Close";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "@store/store";
import type { RootReducerType } from "@store/store";
import MapPanel from "./components/MapPanel";
import { AmenitiesSectionROW } from "./components/AmenitiesSectionROW";
import { SiteRoadwaysROW } from "./components/SiteRoadwaysROW";
import UploadDocumentsROW from "./components/UploadDocumentsROW";
import { setAmenityROWField } from "./redux/amenitesROWSlice";
import { useGeolocation } from "@gis/components/useGeoLocation";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { UnderTakingConsentRow } from "./components/UnderTakingConsentRow";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useNavigate } from "react-router-dom";
import { CircleRateRow } from "./components/CircleRateRow";
import { setCoordinateSite } from "./redux/stepperROWSlice";
import { resetApplicationSubmitted } from "@modules/application-section/application-details/redux/applicationSubmittedSlice";
import { CircleRateRowNew } from "./components/CircleRateRowNew";

const steps = [
  { label: "Utility & Location Details", icon: <AddLocationAlt /> },
  { label: "Site & Highways Information", icon: <AddCard /> },
  { label: "Upload Documents", icon: <UploadFile /> },
  { label: "Application Preview", icon: <VisibilityIcon /> },
  { label: "Undertakings & Consents", icon: <ReceiptLongIcon /> },
  { label: "Submit Application", icon: <ReceiptLongIcon /> },
];

const CustomStepConnector = styled(StepConnector)(() => ({
  [`& .MuiStepConnector-line`]: {
    display: "none",
  },
}));

const CustomStepIcon: React.FC<any> = (props) => {
  const { active, completed, icon } = props;
  const stepIconIndex = Number(icon) - 1;
  const iconClass = completed
    ? "step-icon completed"
    : active
      ? "step-icon active"
      : "step-icon";

  return (
    <div className="step-icon-wrapper">
      <div className={iconClass}>{steps[stepIconIndex]?.icon}</div>
      <span className={`step-label ${icon === 4 ? "" : "text-with-line"}`}>
        {steps[stepIconIndex]?.label}
      </span>
    </div>
  );
};

const StepperComponentROW: React.FC = () => {
  const {
    coordinates: geoCoordinates,
    error,
    isLoading,
  } = useGeolocation({
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
  });

  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  const [isProcessing, setIsProcessing] = useState(false);
  const [mapChainage, setMapChainage] = useState({
    startChainage: "",
    endChainage: "",
  });

  const {
    start_latitude,
    start_longitude,
    end_latitude,
    end_longitude,
    highway_side,
    start_chainage_km,
    start_chainage_m,
    end_chainage_km,
    end_chainage_m,
    isFirstSectionCompletedRow,
  } = useSelector((state: RootReducerType) => state.amenitiesROW);
  const dashboard = useSelector((state: any) => state.applicationDashboard);

  const { activeStep, coordinateSite } = useSelector(
    (state: RootReducerType) => state.stepperROW,
  );
  const isApplicationSubmitted = useSelector(
    (state: RootReducerType) =>
      state.isApplicationSubmitted?.isApplicationSubmitted,
  );

  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false);

  const toggleSidePanel = () => {
    setIsSidePanelCollapsed((prev) => !prev);
  };

  React.useEffect(() => {
    if (activeStep === 3) {
      dispatch(resetApplicationSubmitted());
      navigate("/application-preview-details-row");
      return;
    }
    if (activeStep > 0) {
      setIsSidePanelCollapsed(true);
    } else {
      setIsSidePanelCollapsed(false);
    }
    // dispatch(setCoordinateSite("start"));
  }, [activeStep]);

  const handleMapClick = React.useCallback(
    (coords: any, chainage: any) => {
      const [kmStr, mStr] = chainage?.split("+") || [];
      dispatch(
        setAmenityROWField({
          name: `${coordinateSite}_latitude`,
          value: coords.latitude ?? "",
        }),
      );
      dispatch(
        setAmenityROWField({
          name: `${coordinateSite}_longitude`,
          value: coords.longitude ?? "",
        }),
      );
      dispatch(
        setAmenityROWField({
          name: `${coordinateSite}_chainage_km`,
          value: kmStr || 0,
        }),
      );
      dispatch(
        setAmenityROWField({
          name: `${coordinateSite}_chainage_m`,
          value: mStr || 0,
        }),
      );
      dispatch(setCoordinateSite("end"));
    },
    [dispatch, coordinateSite],
  );

  const start_coordinates = React.useMemo(
    () => ({
      latitude: start_latitude ?? 0,
      longitude: start_longitude ?? 0,
    }),
    [start_latitude, start_longitude],
  );

  const end_coordinates = React.useMemo(
    () => ({
      latitude: end_latitude ?? 0,
      longitude: end_longitude ?? 0,
    }),
    [end_latitude, end_longitude],
  );

  React.useEffect(() => {
    if (isApplicationSubmitted) {
      navigate(`/application-details-row`, { replace: true });
    }
  }, []);

  if (!dashboard) {
    return null;
  }
  const { application_code } = dashboard;

  const breadcrumbData = [
    {
      label: application_code ? application_code : "New Application",
      link: "#",
    },
  ];

  return (
    <div className="page-content">
      {/* <DashboardHeader /> */}
      <GenericBreadCrums breadcrumbs={breadcrumbData} className="mt-6" />

      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={2}
        sx={{
          marginTop: "1rem",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "flex-start", sm: "center" },
        }}
      >
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ fontSize: { xs: "14px", md: "18px" } }}
        >
          Right&nbsp;of&nbsp;Way&nbsp;Permission
        </Typography>
        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: "none", sm: "block" } }}
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            overflowX: "auto",
          }}
        >
          {steps.map((step, index) => (
            <React.Fragment key={index}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor:
                    activeStep >= index
                      ? "rgba(58, 28, 115, 0.24)"
                      : "rgba(0, 0, 0, 0.05)",
                  color:
                    activeStep >= index
                      ? "rgba(58, 28, 115, 1)"
                      : "rgba(117, 117, 117, 1)",
                  border:
                    activeStep === index
                      ? "1px solid rgba(58, 28, 115, 1)"
                      : "1px solid rgba(0, 0, 0, 0.08)",
                  padding: { xs: "4px 4px", sm: "5px 6px" },
                  borderRadius: "25px",
                  minWidth: "fit-content",
                  transition: "all 0.3s ease",
                  mb: { xs: 1, sm: 0 },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    backgroundColor:
                      activeStep >= index
                        ? "rgba(58, 28, 115, 1)"
                        : "rgba(0, 0, 0, 0.08)",
                    color: activeStep >= index ? "#fff" : "#757575",
                    fontSize: "10px",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>
                <Typography
                  sx={{
                    fontSize: "10px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    textAlign: { xs: "center", sm: "left" },
                  }}
                >
                  {step.label}
                </Typography>
              </Box>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    width: "20px",
                    height: "1px",
                    backgroundColor: "#d0d0d0",
                    mx: 0.5,
                    display: { xs: "none", sm: "block" },
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
      </Box>
      <Box className="stepper-container">
        <Box className="step-body-wrapper">
          <Box
            className="step-body mx-auto position-relative"
            sx={{ flexDirection: { xs: "column", sm: "row" } }}
          >
            <Box className="left-panel">
              {activeStep === 0 && (
                <AmenitiesSectionROW
                  collapsed={isSidePanelCollapsed}
                  onChainageChange={setMapChainage}
                />
              )}
              {activeStep === 1 && <SiteRoadwaysROW />}
              {activeStep === 2 && (
                <UploadDocumentsROW collapsed={isSidePanelCollapsed} />
              )}
              {activeStep === 4 && (
                <UnderTakingConsentRow collapsed={isSidePanelCollapsed} />
              )}
              {activeStep === 5 && (
                <CircleRateRowNew collapsed={isSidePanelCollapsed} />
              )}
            </Box>
            {!isSidePanelCollapsed && (
              <MapPanel
                hideRemovePin={false}
                startChainage={
                  mapChainage.startChainage ||
                  `${start_chainage_km || 0}+${start_chainage_m || 0}`
                }
                endChainage={
                  mapChainage.endChainage ||
                  `${end_chainage_km || 0}+${end_chainage_m || 0}`
                }
                geoCoordinates={geoCoordinates}
                start_coordinates={start_coordinates}
                end_coordinates={end_coordinates}
                onMapClick={handleMapClick}
                isFirstSectionCompletedRow={isFirstSectionCompletedRow}
              />
            )}
          </Box>
          {false && (
            <Box className="toggle-map" onClick={toggleSidePanel}>
              {isSidePanelCollapsed ? (
                <MapIcon className="map-toggle-icon" />
              ) : (
                <CloseIcon className="map-toggle-icon" />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </div>
  );
};

export default StepperComponentROW;
